/**
 * FAQSection
 *
 * Disclosure-based FAQ list with sensible defaults. Accepts a `faqs` array
 * of { question, answer } and renders accordions.
 */
import React from 'react';
import { Disclosure } from '@headlessui/react';
import { Plus, Minus } from 'lucide-react';

export default function FAQSection({ faqs }) {
  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 md:grid-cols-[300px,1fr]">
      <div className="py-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Support Center</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Find answers to commonly asked questions about using Amplify.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Frequently asked questions
        </h2>
        <dl className="mt-8 space-y-6 divide-y divide-gray-200 dark:divide-gray-800">
          {faqs.map((faq) => (
            <Disclosure as="div" key={faq.question} className="pt-6 first:pt-0">
              {({ open }) => (
                <>
                  <dt>
                    <Disclosure.Button className="flex w-full items-start justify-between text-left">
                      <span className="text-sm font-normal text-gray-800 dark:text-gray-200">
                        {faq.question}
                      </span>
                      <span className="ml-6 flex h-7 items-center">
                        {open ? (
                          <Minus className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <Plus className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className="mt-2 pr-12">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {faq.answer}
                    </p>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </dl>
      </div>
    </div>
  );
}

// Default FAQs that will be shown if no faqs prop is provided
FAQSection.defaultProps = {
  faqs: [
    {
      question: "How can I delete my account and what happens to my subscription?",
      answer: "To delete your account, go to the Account page and scroll down to the bottom. You'll find the account deletion option there. When you delete your account, your subscription will be canceled immediately—you won't be charged anymore for future billing cycles. However, you'll also lose access to all services, data, and content associated with your account immediately. This action cannot be undone, so please back up any important data before proceeding."
    },
    {
      question: "What AI tools are available and what can I use them for?",
      answer: "We offer several powerful AI tools to help with your campaign content: Copy Generator creates optimized ad copy from your images or prompts; Copy Analyzer evaluates your existing copy and provides suggestions for improvement; and Creative Analyzer examines your visual assets and offers detailed insights on their effectiveness. These tools help you create more engaging campaign content, understand audience engagement factors, and optimize your messaging across platforms—all powered by advanced AI trained on thousands of successful political campaigns."
    },
    {
      question: "What are namespaces and what can I do with them?",
      answer: "Namespaces are organizational workspaces that help you manage your campaigns and content across teams or projects. With namespaces, you can create separate workspaces for different clients, campaigns, or departments. Each namespace has its own set of members with customizable permissions (admin or viewer). Admins can invite team members, manage access, and organize content specifically for that workspace, while keeping it separate from other projects."
    },
    {
      question: "How do I manage my namespaces?",
      answer: "You can manage your namespaces by navigating to the Namespaces section in your dashboard. There, you can create new namespaces, edit existing ones, invite team members, and set permissions. Each namespace maintains its own separate content and settings, allowing you to organize your work efficiently across different projects or clients."
    }
  ]
};